import { useCallback } from 'react';
import { defaultT, type TFunc, type TVars, type WidgetTheme } from '@optiprime/referral-widget';
import i18n from '@/lib/i18n';

/**
 * Brand the portable referral widget with OptiSigns' design tokens. We pass the
 * host's CSS custom properties (NOT hardcoded hex) so the widget inherits the
 * live theme — including dark mode, since every `--color-*` token is redefined
 * under `.dark` in globals.css. The widget maps each to its own `--ref-*` var.
 *
 * `launcherBg` is a light surface ON PURPOSE — NOT the OptiBot gradient. The pill
 * stacks directly above the chat FAB, so it must read as the SECONDARY launcher,
 * not a gradient twin of it. The rest of the secondary treatment (smaller size,
 * teal gift icon, teal hairline border) lives in `referral-widget-overrides.css`
 * because the widget doesn't tokenize launcher size/border.
 *
 * Single source of truth: both the floating launcher (`ReferralWidgetMount`)
 * and the full referral page (`ReferralPage`) import this so the page reached
 * from the account menu is themed identically to the pill flow.
 */
export const OPTISIGNS_THEME: WidgetTheme = {
  accent: 'var(--color-primary)',
  accentContrast: 'var(--color-primary-foreground)',
  launcherBg: 'var(--color-card)',
  // Elevated surface, not card: identical #ffffff in light, but in dark the
  // popover token (#21222e) lifts the modal off the #0e0f13 page the way
  // scio3's other floating panels do — card (#15161e) blended into it (#1599).
  surface: 'var(--color-popover)',
  // Host-flipped text tokens (defined in referral-widget-overrides.css): light
  // resolves to the app's regular foreground/muted values; dark lifts both a
  // step whiter — the app-wide #ced6df/#8495ab read too grey inside the
  // elevated modal (PM feedback on the #1599 dark polish).
  text: 'var(--scio3-ref-text)',
  muted: 'var(--scio3-ref-muted)',
  border: 'var(--color-border)',
  success: 'var(--color-success)',
  radius: 'var(--radius-md)',
  // Host-flipped shadow (defined in referral-widget-overrides.css): the widget's
  // light-tuned default is invisible on dark backgrounds.
  shadow: 'var(--scio3-ref-shadow)',
  font: 'inherit',
};

/**
 * Static i18n registry for the portable referral widget.
 *
 * The widget calls our `t` adapter with its own namespaced keys (`pill.label`,
 * `modal.send`, …) and we resolve them under the host's flat `referral.*` keys.
 * We must NOT do this with a dynamic ``i18n.t(`referral.${key}`)`` call: the
 * Lokalise code-scanner (CI → `syncscio30-code-to-beta`) only discovers keys
 * written as string LITERALS, so a template-literal key would never sync to
 * Lokalise and the strings would never get translated into the other 8 languages
 * (`.claude/rules/frontend/react-i18n.md` — "no dynamic key construction; use a
 * lookup map instead", mirroring `EnterpriseWifiSection`'s `STATUS_KEY`).
 *
 * Every key in the widget's `defaultT` contract is therefore enumerated below as
 * a literal `i18n.t('referral.<key>', …)` call. `defaultValue: defaultT(...)`
 * keeps the widget's bundled English as a fallback when the host key is absent —
 * so a key a future widget version adds (before it's mirrored in `en.json`) still
 * renders English instead of a raw key. Keep this map in sync with the widget
 * bundle. Unknown keys (not listed) fall through to `defaultT` in `useReferralT`.
 */
const REFERRAL_STRINGS: Record<string, (vars?: TVars) => string> = {
  'banner.invited': (v) =>
    i18n.t('referral.banner.invited', { ...v, defaultValue: defaultT('banner.invited', v) }),
  'banner.invitedBy': (v) =>
    i18n.t('referral.banner.invitedBy', { ...v, defaultValue: defaultT('banner.invitedBy', v) }),
  'banner.reward': (v) =>
    i18n.t('referral.banner.reward', { ...v, defaultValue: defaultT('banner.reward', v) }),
  'pill.label': (v) =>
    i18n.t('referral.pill.label', { ...v, defaultValue: defaultT('pill.label', v) }),
  'pill.dismiss': (v) =>
    i18n.t('referral.pill.dismiss', { ...v, defaultValue: defaultT('pill.dismiss', v) }),
  'modal.close': (v) =>
    i18n.t('referral.modal.close', { ...v, defaultValue: defaultT('modal.close', v) }),
  'modal.intro': (v) =>
    i18n.t('referral.modal.intro', { ...v, defaultValue: defaultT('modal.intro', v) }),
  'modal.inviteLabel': (v) =>
    i18n.t('referral.modal.inviteLabel', { ...v, defaultValue: defaultT('modal.inviteLabel', v) }),
  'modal.emailAria': (v) =>
    i18n.t('referral.modal.emailAria', { ...v, defaultValue: defaultT('modal.emailAria', v) }),
  'modal.emailPlaceholder': (v) =>
    i18n.t('referral.modal.emailPlaceholder', {
      ...v,
      defaultValue: defaultT('modal.emailPlaceholder', v),
    }),
  'modal.emailHint': (v) =>
    i18n.t('referral.modal.emailHint', { ...v, defaultValue: defaultT('modal.emailHint', v) }),
  'modal.send': (v) =>
    i18n.t('referral.modal.send', { ...v, defaultValue: defaultT('modal.send', v) }),
  'modal.shareLabel': (v) =>
    i18n.t('referral.modal.shareLabel', { ...v, defaultValue: defaultT('modal.shareLabel', v) }),
  'modal.shareDivider': (v) =>
    i18n.t('referral.modal.shareDivider', {
      ...v,
      defaultValue: defaultT('modal.shareDivider', v),
    }),
  'modal.linkAria': (v) =>
    i18n.t('referral.modal.linkAria', { ...v, defaultValue: defaultT('modal.linkAria', v) }),
  'modal.copyLink': (v) =>
    i18n.t('referral.modal.copyLink', { ...v, defaultValue: defaultT('modal.copyLink', v) }),
  'modal.copyLinkBtn': (v) =>
    i18n.t('referral.modal.copyLinkBtn', { ...v, defaultValue: defaultT('modal.copyLinkBtn', v) }),
  'modal.copied': (v) =>
    i18n.t('referral.modal.copied', { ...v, defaultValue: defaultT('modal.copied', v) }),
  'confirmation.title': (v) =>
    i18n.t('referral.confirmation.title', {
      ...v,
      defaultValue: defaultT('confirmation.title', v),
    }),
  'confirmation.sentTo': (v) =>
    i18n.t('referral.confirmation.sentTo', {
      ...v,
      defaultValue: defaultT('confirmation.sentTo', v),
    }),
  'confirmation.body': (v) =>
    i18n.t('referral.confirmation.body', { ...v, defaultValue: defaultT('confirmation.body', v) }),
  'confirmation.rewardEach': (v) =>
    i18n.t('referral.confirmation.rewardEach', {
      ...v,
      defaultValue: defaultT('confirmation.rewardEach', v),
    }),
  'confirmation.reward': (v) =>
    i18n.t('referral.confirmation.reward', {
      ...v,
      defaultValue: defaultT('confirmation.reward', v),
    }),
  'confirmation.cappedTitle': (v) =>
    i18n.t('referral.confirmation.cappedTitle', {
      ...v,
      defaultValue: defaultT('confirmation.cappedTitle', v),
    }),
  'confirmation.capped': (v) =>
    i18n.t('referral.confirmation.capped', {
      ...v,
      defaultValue: defaultT('confirmation.capped', v),
    }),
  'confirmation.cappedOne': (v) =>
    i18n.t('referral.confirmation.cappedOne', {
      ...v,
      defaultValue: defaultT('confirmation.cappedOne', v),
    }),
  'confirmation.cappedAtLimit': (v) =>
    i18n.t('referral.confirmation.cappedAtLimit', {
      ...v,
      defaultValue: defaultT('confirmation.cappedAtLimit', v),
    }),
  'confirmation.more': (v) =>
    i18n.t('referral.confirmation.more', { ...v, defaultValue: defaultT('confirmation.more', v) }),
  'confirmation.done': (v) =>
    i18n.t('referral.confirmation.done', { ...v, defaultValue: defaultT('confirmation.done', v) }),
  'dashboard.title': (v) =>
    i18n.t('referral.dashboard.title', { ...v, defaultValue: defaultT('dashboard.title', v) }),
  'dashboard.code': (v) =>
    i18n.t('referral.dashboard.code', { ...v, defaultValue: defaultT('dashboard.code', v) }),
  'dashboard.link': (v) =>
    i18n.t('referral.dashboard.link', { ...v, defaultValue: defaultT('dashboard.link', v) }),
  'dashboard.copyCode': (v) =>
    i18n.t('referral.dashboard.copyCode', {
      ...v,
      defaultValue: defaultT('dashboard.copyCode', v),
    }),
  'dashboard.statInvited': (v) =>
    i18n.t('referral.dashboard.statInvited', {
      ...v,
      defaultValue: defaultT('dashboard.statInvited', v),
    }),
  'dashboard.statInProgress': (v) =>
    i18n.t('referral.dashboard.statInProgress', {
      ...v,
      defaultValue: defaultT('dashboard.statInProgress', v),
    }),
  'dashboard.statConverted': (v) =>
    i18n.t('referral.dashboard.statConverted', {
      ...v,
      defaultValue: defaultT('dashboard.statConverted', v),
    }),
  'dashboard.statCredit': (v) =>
    i18n.t('referral.dashboard.statCredit', {
      ...v,
      defaultValue: defaultT('dashboard.statCredit', v),
    }),
  'dashboard.walletApplied': (v) =>
    i18n.t('referral.dashboard.walletApplied', {
      ...v,
      defaultValue: defaultT('dashboard.walletApplied', v),
    }),
  'dashboard.walletPending': (v) =>
    i18n.t('referral.dashboard.walletPending', {
      ...v,
      defaultValue: defaultT('dashboard.walletPending', v),
    }),
  'dashboard.walletNote': (v) =>
    i18n.t('referral.dashboard.walletNote', {
      ...v,
      defaultValue: defaultT('dashboard.walletNote', v),
    }),
  'dashboard.recent': (v) =>
    i18n.t('referral.dashboard.recent', { ...v, defaultValue: defaultT('dashboard.recent', v) }),
};

/**
 * Bridge scio3 i18n → the widget's `TFunc`. Resolves the widget's namespaced key
 * through the static {@link REFERRAL_STRINGS} registry (every key a literal the
 * Lokalise scanner can find). An unknown key — e.g. one a newer widget version
 * introduces before it's added here — falls through to the widget's bundled English.
 */
export function useReferralT(): TFunc {
  return useCallback((key: string, vars?: TVars) => {
    const resolve = REFERRAL_STRINGS[key];
    return resolve ? resolve(vars) : defaultT(key, vars);
  }, []);
}
